import type { ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { AuthLayout, OtpVerificationStep } from '@/modules/auth/ui/components'
import { formatTryAgainLabel } from '@/shared/ui/hooks'
import { Button, Input } from '@/shared/ui/primitives'
import type { SendOtp, VerifyOtp } from '@/modules/auth/use-cases'
import { useSignupPage } from './hooks/use-signup-page'

export type SignupPageProps = {
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
}

export function SignupPage({ onSendOtp, onVerifyOtp }: SignupPageProps) {
  const vm = useSignupPage({ onSendOtp })

  if (vm.step === 'otp') {
    return (
      <OtpVerificationStep
        identifier={vm.identifier}
        channel="email"
        onSendOtp={onSendOtp}
        onVerifyOtp={onVerifyOtp}
        onVerifySuccess={vm.handleOtpSuccess}
        onBack={vm.backToIdentifier}
      />
    )
  }

  return (
    <AuthLayout
      title="Welcome to BlindBook"
      subtitle="Create your club manager account to get started"
    >
      <form onSubmit={vm.handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1">
          <Input
            id="email"
            type="email"
            aria-label="Email"
            value={vm.email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            autoFocus
          />
        </div>
        <p className="text-xs text-muted-foreground">We&apos;ll send you a 6-digit verification code</p>
        {vm.hasError ? (
          <p role="alert" className="text-sm text-destructive">
            {vm.errorMessage}
          </p>
        ) : null}
        <Button type="submit" disabled={vm.isSubmitting || vm.isCooldownActive} className="w-full">
          {vm.isSubmitting
            ? 'Sending…'
            : vm.isCooldownActive
              ? formatTryAgainLabel(vm.cooldownSeconds)
              : 'Continue with Email'}
        </Button>
        <div data-testid="signup-footer" className="mt-2 text-center text-xs text-muted-foreground">
          <p>
            Already have an account?{' '}
            <Link to="/sign-in" className="font-medium text-foreground underline hover:no-underline">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  )
}
